import { useMemo, useEffect, useRef, useCallback } from "react";

import { useFormikContext } from "formik";

import { useFlags } from "flagsmith/react";
import { USER_FLAGS_ENUM } from "@utils/featureFlags";
import { filterBlockedProducts } from "@domain/policies/productBlockPolicy";

import type { FormikInitialValuesNewRequest } from "../../model/form.model";
import type { PaymentMethod } from "../../model/product.cart.model";

import { useNewRequestContext } from "../../state/NewRequestProvider";

import { addProductToCart } from "../../application/usecases/products/addProductToCart";
import { repriceCartItem } from "../../application/usecases/products/repriceCartItem";
import { GetQuantityOptionsUseCase } from "../../application/usecases/products/quantityOptions";
import { applyTaxAccordingFreight } from "../../domain/calculations/applyTaxAccordingFreight";

export type CatalogOption = {
  key: string;
  label: string;
  code?: string;
};

export function useProductsStep() {
  const flag = useFlags([
    USER_FLAGS_ENUM.USER_NEW_REQUEST_LIST_AMOUNT,
    USER_FLAGS_ENUM.BLOCK_NEW_REQUEST_PRODUCTS,
    USER_FLAGS_ENUM.CASH_PAYMENT,
  ]);
  const blockFlag = flag[USER_FLAGS_ENUM.BLOCK_NEW_REQUEST_PRODUCTS];
  const isBlockEnabled = blockFlag?.enabled === true;
  const blockFlagValue = blockFlag?.value as string | undefined;

  const isCashPaymentEnabled =
    flag[USER_FLAGS_ENUM.CASH_PAYMENT]?.enabled === true;

  const { services, state, dispatch } = useNewRequestContext();

  const formik = useFormikContext<FormikInitialValuesNewRequest>();

  const cart = state.products.cart;
  const pricingStatus = state.products.pricingStatus;
  const error = state.products.error;

  const freightType = formik.values.purchase.freightType;
  const purchase = formik.values.purchase;

  const cartView = useMemo(() => {
    return cart.map((it) => {
      const displayUnitPrice = applyTaxAccordingFreight(
        freightType,
        it.unitPrice,
        it.freightUnitPrice
      );

      return {
        ...it,
        displayUnitPrice,
      };
    });
  }, [cart, freightType]);

  const totals = useMemo(() => {
    const subtotal = cart.reduce(
      (acc, it) => acc + (it.quantity > 0 ? it.subtotal : 0),
      0
    );
    const total = cart.reduce(
      (acc, it) => acc + (it.quantity > 0 ? it.total : 0),
      0
    );
    return { subtotal, total };
  }, [cart]);

  const deliveryDate = formik.values.purchase.deliveryDate;

  const catalogOptions: CatalogOption[] = useMemo(() => {
    const allProducts = state.purchase.customer?.products ?? [];
    const filtered = filterBlockedProducts({
      products: allProducts,
      deliveryDate: deliveryDate || undefined,
      isFeatureEnabled: isBlockEnabled,
      flagValue: blockFlagValue,
    });
    return filtered.map((p) => ({
      key: p.id,
      label: p.description,
    }));
  }, [
    state.purchase.customer?.products,
    deliveryDate,
    isBlockEnabled,
    blockFlagValue,
  ]);

  const listAmount = flag.user_new_request_create_option_list_amount;
  const quantityOptions = useMemo(() => {
    return new GetQuantityOptionsUseCase(listAmount).execute();
  }, [listAmount]);

  // Ref para acessar o cart atual sem incluí-lo nas deps dos handlers,
  // evitando race conditions quando cart muda por operações do usuário.
  const cartRef = useRef(cart);
  cartRef.current = cart;

  // Guard reutilizável: descarta respostas obsoletas nos 3 handlers de reprecificação
  const repriceGuard = useStaleGuard();

  const fetchPriceRequestId = useRef(0);

  const priceSumServiceRef = useRef(services.priceSumService);
  priceSumServiceRef.current = services.priceSumService;

  const fetchTotalPrice = useCallback(
    async (nextCart: typeof cart) => {
      const requestId = ++fetchPriceRequestId.current;

      const subtotals = nextCart
        .filter((item) => item.quantity > 0 && item.subtotal != null)
        .map((item) => String(item.subtotal));

      if (subtotals.length === 0) {
        dispatch({ type: "products/setTotalPrice", value: null });
        return;
      }

      try {
        const sum = await priceSumServiceRef.current.execute(subtotals);
        if (requestId !== fetchPriceRequestId.current) return; // resposta obsoleta
        dispatch({ type: "products/setTotalPrice", value: sum });
      } catch (err) {
        if (requestId !== fetchPriceRequestId.current) return;
        console.error("[fetchTotalPrice] falhou:", err);
        dispatch({ type: "products/setTotalPrice", value: null });
        dispatch({ type: "products/setPricingStatus", value: "error" });
      }
    },
    [dispatch]
  );

  async function addProduct(input: {
    productId: string;
    code: string;
    description: string;
    quantity: number;
  }) {
    dispatch({ type: "products/setError", value: undefined });
    dispatch({ type: "products/setPricingStatus", value: "loading" });

    try {
      const customer = state.purchase.customer;
      if (!customer?.id) throw new Error("Selecione um cliente.");

      const address = customer.addresses?.find(
        (a) => a.id === purchase.deliveryAddressId
      );
      if (!address) throw new Error("Selecione um endereço de entrega.");

      const filialId = state.purchase.customer?.withdrawBasis.find(
        (base) =>
          String(base.shippingPoint) === String(purchase.withdrawBasisId)
      )?.filialID;

      if (!filialId) throw new Error("Selecione uma base de retirada válida.");

      const next = await addProductToCart({
        services,
        freightType: purchase.freightType,
        customer: { id: customer.id },
        filialId,
        address: {
          id: address.id,
          transportationZone: String(address.transportationZone ?? ""),
        },
        currentCart: cart,
        input,
        products: customer.products,
        lastPayment: state.lastPayment,
        isCashPaymentEnabled,
      });

      dispatch({ type: "products/setCart", value: next });
      dispatch({ type: "products/setPricingStatus", value: "success" });
      await fetchTotalPrice(next);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro ao adicionar produto.";
      dispatch({ type: "products/setError", value: msg });
      dispatch({ type: "products/setPricingStatus", value: "error" });
    }
  }

  async function changeQty(itemId: string, qtyRaw: string) {
    dispatch({ type: "products/setError", value: undefined });
    dispatch({ type: "products/setPricingStatus", value: "loading" });

    const requestId = repriceGuard.next();

    try {
      const next = await repriceCartItem({
        services,
        freightType: purchase.freightType,
        currentCart: cartRef.current,
        targetId: itemId,
        patch: { quantity: toInt(qtyRaw) },
      });

      if (repriceGuard.isStale(requestId)) return;

      dispatch({ type: "products/setCart", value: next });
      dispatch({ type: "products/setPricingStatus", value: "success" });
      await fetchTotalPrice(next);
    } catch (e) {
      if (repriceGuard.isStale(requestId)) return;
      const msg =
        e instanceof Error ? e.message : "Erro ao atualizar quantidade.";
      dispatch({ type: "products/setError", value: msg });
      dispatch({ type: "products/setPricingStatus", value: "error" });
    }
  }

  function remove(itemId: string) {
    dispatch({ type: "products/setError", value: undefined });

    const next = cart.filter((c) => c.id !== itemId);
    dispatch({ type: "products/setCart", value: next });
    void fetchTotalPrice(next);
  }

  const canInteract = useMemo(() => {
    return Boolean(
      purchase.customerCnpj &&
        purchase.withdrawBasisId &&
        purchase.deliveryAddressId
    );
  }, [
    purchase.customerCnpj,
    purchase.withdrawBasisId,
    purchase.deliveryAddressId,
  ]);

  async function changePayment(itemId: string, payment: PaymentMethod) {
    dispatch({ type: "products/setError", value: undefined });
    dispatch({ type: "products/setPricingStatus", value: "loading" });

    const requestId = repriceGuard.next();

    try {
      const currentCart = cartRef.current;
      const target = currentCart.find((c) => c.id === itemId);

      // Ao trocar para "B" (prazo), usar payCondMax como default (mesmo comportamento do V1)
      // Se payCondMax === 0 (produto sem prazo configurado), usa 1 dia como fallback
      // para evitar enviar deadline 0 ao backend (que seria interpretado como "à vista").
      // Ref V1: FormContext.tsx → defaultPaymentDaysSelected = "1" quando payCond === 0
      // Ao trocar para "T" (à vista), zerar o deadline
      let deadlinePayment: number | undefined;
      if (payment === "B" && target) {
        deadlinePayment = target.payCondMax > 0 ? target.payCondMax : 1;
      } else if (payment === "T") {
        deadlinePayment = 0;
      }

      const next = await repriceCartItem({
        services,
        freightType: purchase.freightType,
        currentCart,
        targetId: itemId,
        patch: { payment, deadlinePayment },
      });

      if (repriceGuard.isStale(requestId)) return;

      dispatch({ type: "products/setCart", value: next });
      dispatch({ type: "products/setPricingStatus", value: "success" });
      await fetchTotalPrice(next);
    } catch (e) {
      if (repriceGuard.isStale(requestId)) return;
      const msg =
        e instanceof Error ? e.message : "Erro ao atualizar pagamento.";
      dispatch({ type: "products/setError", value: msg });
      dispatch({ type: "products/setPricingStatus", value: "error" });
    }
  }

  async function changeDeadline(itemId: string, deadlinePaymentRaw: string) {
    dispatch({ type: "products/setError", value: undefined });
    dispatch({ type: "products/setPricingStatus", value: "loading" });

    const requestId = repriceGuard.next();

    try {
      const newDeadline = toInt(deadlinePaymentRaw);

      const newPayment = newDeadline > 0 ? "B" : "T";
      const next = await repriceCartItem({
        services,
        freightType: purchase.freightType,
        currentCart: cartRef.current,
        targetId: itemId,
        patch: {
          deadlinePayment: newDeadline,
          payment: newPayment,
        },
      });

      if (repriceGuard.isStale(requestId)) return;

      dispatch({ type: "products/setCart", value: next });
      dispatch({ type: "products/setPricingStatus", value: "success" });
      await fetchTotalPrice(next);
    } catch (e) {
      if (repriceGuard.isStale(requestId)) return;
      const msg = e instanceof Error ? e.message : "Erro ao atualizar prazo.";
      dispatch({ type: "products/setError", value: msg });
      dispatch({ type: "products/setPricingStatus", value: "error" });
    }
  }

  const prevFreightRef = useRef(freightType);
  // requestId protege contra múltiplas séries de requests se o usuário
  // trocar o frete rapidamente — apenas a última série despacha resultados.
  const repriceRequestId = useRef(0);

  useEffect(() => {
    if (prevFreightRef.current === freightType) return;
    prevFreightRef.current = freightType;

    const currentCart = cartRef.current;
    if (!currentCart.length || !canInteract) return;

    const requestId = ++repriceRequestId.current;
    let cancelled = false;

    async function repriceAll() {
      dispatch({ type: "products/setError", value: undefined });
      dispatch({ type: "products/setPricingStatus", value: "loading" });

      try {
        const itemsToReprice = currentCart.filter((item) => item.quantity > 0);

        // Reprecifica em paralelo com limite de concorrência para não
        // sobrecarregar o backend enquanto reduz o tempo total de espera.
        const CONCURRENCY = 3;
        const repricedItems = await promiseAllWithConcurrency(
          itemsToReprice.map((item) => async () => {
            if (cancelled || requestId !== repriceRequestId.current)
              return null;

            const result = await repriceCartItem({
              services,
              freightType,
              currentCart: [item],
              targetId: item.id,
              patch: {},
            });
            return result[0];
          }),
          CONCURRENCY
        );

        // Descarta resultado se já existe uma série mais recente ou unmount
        if (cancelled || requestId !== repriceRequestId.current) return;

        // Monta o cart atualizado: substitui items reprecificados, mantém os que não precisavam
        const repricedMap = new Map(
          repricedItems
            .filter((item): item is NonNullable<typeof item> => item != null)
            .map((item) => [item.id, item])
        );

        const updated = currentCart.map(
          (item) => repricedMap.get(item.id) ?? item
        );

        dispatch({ type: "products/setCart", value: updated });
        dispatch({ type: "products/setPricingStatus", value: "success" });
        await fetchTotalPrice(updated);
      } catch (e) {
        if (cancelled || requestId !== repriceRequestId.current) return;
        const msg =
          e instanceof Error ? e.message : "Erro ao recalcular frete.";
        dispatch({ type: "products/setError", value: msg });
        dispatch({ type: "products/setPricingStatus", value: "error" });
      }
    }

    void repriceAll();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freightType, canInteract, dispatch, services, fetchTotalPrice]);

  return {
    state: {
      cart,
      cartView,
      totals,
      pricingStatus,
      error,
      canInteract: canInteract,
      catalogOptions,
      quantityOptions,
      freightType,
      isCashPaymentEnabled,
    },
    action: {
      changePayment,
      changeDeadline,
      addProduct,
      changeQty,
      remove,
    },
  };
}

function toInt(v: string): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : 0;
}

// ex: 1000 ou "1000" => "1.000"
export function formatQty(qty?: number | string): string {
  if (qty === undefined || qty === null) return "-";

  const value = typeof qty === "string" ? parseFloat(qty) : qty;

  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

// ex: "1.000" => "1000"
export function parseDotQuantity(str: string): string {
  return String(Number(str.replace(/\./g, "")));
}

async function promiseAllWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  concurrency: number
): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let index = 0;

  async function worker() {
    while (index < tasks.length) {
      const i = index++;
      results[i] = await tasks[i]();
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, tasks.length) },
    () => worker()
  );

  await Promise.all(workers);
  return results;
}

function useStaleGuard() {
  const ref = useRef(0);

  function next(): number {
    return ++ref.current;
  }

  function isStale(id: number): boolean {
    return id !== ref.current;
  }

  return { next, isStale };
}
